// 조직 이용 상태(정상·이용 중지·해지·승인 대기) → 접근 수준(전부·조회만·막힘) — api 문지기·고객 링크·간편접수·cron·web·운영자 콘솔이 이 한 벌로 판정한다.

import { SUPPORT_EMAIL } from "../../lib/support-email";

export const ORG_STATUSES = ["active", "suspended", "terminated", "pending"] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

export const ORG_STATUS_LABEL: Record<OrgStatus, string> = {
  active: "정상",
  suspended: "이용 중지",
  terminated: "해지",
  pending: "승인 대기",
};

export const isOrgStatus = (value: unknown): value is OrgStatus =>
  typeof value === "string" && (ORG_STATUSES as readonly string[]).includes(value);

// 미납 이용 중지 뒤 조회만 되는 날 수 — 지나면 전부 막힌다.
export const SUSPEND_READ_ONLY_DAYS = 30;
// 해지 뒤 그대로 복구할 수 있는 날 수 — 지나면 파기 cron(`modules/org/purge`)이 지운다.
export const TERMINATE_RESTORE_DAYS = 90;
// 운영자 장부(admin_audit) 개인정보 보관 — 조직 파기 후 이 날수가 지나면 이메일·메모를 지운다(`0116` · 개인정보 처리방침 3절).
export const AUDIT_RETENTION_DAYS = 365;

export type OrgAccessLevel = "full" | "read_only" | "blocked";

export interface OrgAccess {
  level: OrgAccessLevel;
  status: OrgStatus;
  // 조회만 되는 남은 날(read_only 일 때만).
  daysLeft: number | null;
  // 해지 뒤 복구 기한 = 파기 예정일(terminated 일 때만).
  restoreUntil: string | null;
  restorable: boolean;
  // 운영자가 가입을 반려한 해지(status_note 가 ORG_REJECTED_NOTE 로 시작).
  rejected: boolean;
  // 반려 사유(반려일 때만 · 표지 뒤 운영자 메모) — 다른 상태의 메모는 신청자에게 보내지 않는다.
  rejectReason: string | null;
}

export interface OrgStatusFields {
  status?: string | null;
  status_changed_at?: string | null;
  status_note?: string | null;
}

export const DAY_MS = 24 * 60 * 60 * 1000;

// 가입 반려 표지 — 반려는 해지(terminated)로 남기고 메모 앞머리로 가른다(파기는 해지와 같다).
export const ORG_REJECTED_NOTE = "가입 반려";

export const isRejectedNote = (note: string | null | undefined): boolean => (note ?? "").startsWith(ORG_REJECTED_NOTE);

// 반려 메모 구분자 — 「가입 반려 · <사유>」.
export const ORG_NOTE_SEPARATOR = " · ";

// 반려 사유 — 표지 뒤 운영자 메모만 · 반려 메모가 아니거나 사유가 비면 null.
export const rejectReasonOf = (note: string | null | undefined): string | null => {
  if (!isRejectedNote(note)) return null;
  const rest = (note ?? "").slice(ORG_REJECTED_NOTE.length).replace(/^\s*·\s*/, "").trim();
  return rest === "" ? null : rest;
};

// 재신청 표지 — 반려 뒤 대표가 다시 신청한 승인 대기는 메모가 이것으로 시작한다(이전 반려 사유를 뒤에 남긴다).
export const ORG_REAPPLIED_NOTE = "재신청";

export const isReappliedNote = (note: string | null | undefined): boolean => (note ?? "").startsWith(ORG_REAPPLIED_NOTE);

// 재신청 대기 메모에 남긴 이전 반려 사유 — 없으면 null.
export const reappliedPrevReasonOf = (note: string | null | undefined): string | null => {
  if (!isReappliedNote(note)) return null;
  const rest = (note ?? "").slice(ORG_REAPPLIED_NOTE.length).replace(/^\s*·\s*/, "").replace(/^이전 반려 사유:\s*/, "").trim();
  return rest === "" ? null : rest;
};

// 재신청 대기 메모 — 이전 반려 메모에서 사유만 옮긴다.
export const reappliedNoteOf = (prevNote: string | null | undefined): string => {
  const reason = rejectReasonOf(prevNote);
  return reason ? `${ORG_REAPPLIED_NOTE}${ORG_NOTE_SEPARATOR}이전 반려 사유: ${reason}` : ORG_REAPPLIED_NOTE;
};

const FULL: OrgAccess = { level: "full", status: "active", daysLeft: null, restoreUntil: null, restorable: false, rejected: false, rejectReason: null };

const blockedOf = (status: OrgStatus): OrgAccess => ({ level: "blocked", status, daysLeft: null, restoreUntil: null, restorable: false, rejected: false, rejectReason: null });

// 열이 없거나 비면(마이그레이션 전) 정상 · 모르는 값·바뀐 시각 없는 중지/해지는 막힘(fail-closed).
export const orgAccessOf = (org: OrgStatusFields, now: Date): OrgAccess => {
  const raw = org.status ?? "active";
  if (raw === "active") return FULL;
  if (!isOrgStatus(raw)) return blockedOf("terminated");
  if (raw === "pending") return blockedOf("pending");
  const changed = org.status_changed_at ? Date.parse(org.status_changed_at) : Number.NaN;
  if (Number.isNaN(changed)) return blockedOf(raw);
  if (raw === "suspended") {
    const until = changed + SUSPEND_READ_ONLY_DAYS * DAY_MS;
    const left = until - now.getTime();
    if (left <= 0) return blockedOf("suspended");
    return { level: "read_only", status: "suspended", daysLeft: Math.ceil(left / DAY_MS), restoreUntil: null, restorable: true, rejected: false, rejectReason: null };
  }
  const restoreUntil = changed + TERMINATE_RESTORE_DAYS * DAY_MS;
  return {
    level: "blocked",
    status: "terminated",
    daysLeft: null,
    restoreUntil: new Date(restoreUntil).toISOString(),
    restorable: now.getTime() < restoreUntil,
    rejected: isRejectedNote(org.status_note),
    rejectReason: rejectReasonOf(org.status_note),
  };
};

export const ORG_READ_ONLY_TEXT = "결제 확인이 필요해 지금은 조회만 가능합니다";
export const ORG_SUSPENDED_TEXT = "결제가 확인되지 않아 이용이 중지되었습니다";
export const ORG_TERMINATED_TEXT = "해지된 조직입니다";
export const ORG_PENDING_TEXT = "가입 승인을 기다리고 있습니다";
export const ORG_PENDING_GUIDE_TEXT = `운영자가 확인한 뒤 승인되면 메일로 알려 드립니다 · 문의는 ${SUPPORT_EMAIL}`;
export const ORG_REJECTED_TEXT = "가입이 반려되었습니다";
export const ORG_REJECTED_GUIDE_TEXT = `문의는 ${SUPPORT_EMAIL} 로 보내 주세요`;
export const ORG_LINK_UNAVAILABLE_TEXT = "현재 이용할 수 없는 링크입니다";
export const ORG_LINK_READ_ONLY_TEXT = "지금은 열람만 가능한 링크입니다";
export const ORG_RETENTION_TEXT = `해지 후 ${TERMINATE_RESTORE_DAYS}일 안에 ${SUPPORT_EMAIL} 로 요청하면 그대로 복구됩니다 · ${TERMINATE_RESTORE_DAYS}일이 지나면 데이터를 지웁니다`;

export const orgReadOnlyBannerText = (daysLeft: number): string =>
  `결제 확인이 필요합니다 · ${daysLeft}일 뒤 모든 기능이 막힙니다 · 지금은 조회만 가능`;

export const orgBlockedText = (access: Pick<OrgAccess, "status"> & { rejected?: boolean }): string => {
  if (access.status === "pending") return ORG_PENDING_TEXT;
  if (access.status === "terminated") return access.rejected ? ORG_REJECTED_TEXT : ORG_TERMINATED_TEXT;
  return ORG_SUSPENDED_TEXT;
};

// 쓸 수 없는 조직이라 DB 함수가 막았다는 표지(0089) — rpcFail 이 OrgReadOnlyError 로 바꾼다.
export const ORG_NOT_WRITABLE_HINT = "org_not_writable";

// 조회만 되는 조직의 쓰기 — api 가 403 org_read_only 로 돌려준다.
export class OrgReadOnlyError extends Error {
  constructor() {
    super(ORG_READ_ONLY_TEXT);
    this.name = "OrgReadOnlyError";
  }
}

// 막힌 조직의 멤버 요청 — api 가 403 org_blocked 로 돌려준다.
export class OrgBlockedError extends Error {
  constructor(
    readonly status: OrgStatus,
    readonly rejected: boolean = false,
    readonly rejectReason: string | null = null,
  ) {
    super(orgBlockedText({ status, rejected }));
    this.name = "OrgBlockedError";
  }
}

// 막힌 조직의 고객 링크·로그인 없는 경로 — api 가 403 org_unavailable 로 돌려준다.
export class OrgUnavailableError extends Error {
  constructor(message: string = ORG_LINK_UNAVAILABLE_TEXT) {
    super(message);
    this.name = "OrgUnavailableError";
  }
}

// 조회만인 조직의 고객 링크 쓰기(서명·메시지 등) — api 가 403 org_read_only 로 돌려준다.
export class OrgLinkReadOnlyError extends Error {
  constructor() {
    super(ORG_LINK_READ_ONLY_TEXT);
    this.name = "OrgLinkReadOnlyError";
  }
}

export type OrgRequestKind = "read" | "write";

// 멤버 요청 하나의 판정 — 통과면 null, 막으면 던질 오류.
export const memberAccessError = (access: OrgAccess, kind: OrgRequestKind): Error | null => {
  if (access.level === "full") return null;
  if (access.level === "blocked") return new OrgBlockedError(access.status, access.rejected, access.rejectReason);
  return kind === "write" ? new OrgReadOnlyError() : null;
};

// 고객 링크·로그인 없는 요청 하나의 판정 — 통과면 null, 막으면 던질 오류.
export const openAccessError = (access: OrgAccess, kind: OrgRequestKind): Error | null => {
  if (access.level === "full") return null;
  if (access.level === "blocked") return new OrgUnavailableError();
  return kind === "write" ? new OrgLinkReadOnlyError() : null;
};

export const ORG_PAGE_UNAVAILABLE_TEXT = "현재 이용할 수 없는 페이지입니다";
export const ORG_INVITE_UNAVAILABLE_TEXT = "지금은 이 조직에 합류할 수 없습니다 · 대표에게 문의해 주세요";
export const ORG_INTAKE_UNAVAILABLE_TEXT = "지금은 접수를 받을 수 없습니다";

// 공개 페이지(시공사례·홈페이지 링크·간편접수) 판정 — 막힘은 닫고, 조회만은 보기만 연다(쓰기는 닫힘).
export const publicPageError = (access: OrgAccess, kind: OrgRequestKind): Error | null => {
  if (access.level === "full") return null;
  if (access.level === "blocked" || kind === "write") return new OrgUnavailableError(ORG_PAGE_UNAVAILABLE_TEXT);
  return null;
};
